import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface DataPoint {
  name: string;
  value: number;
  description: string;
  icon: string;
}

interface SlideContent {
  title?: string;
  subtitle?: string;
  data?: string | DataPoint[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  teacherNote?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const defaultData: DataPoint[] = [];

interface EditableDivProps {
  text: string;
  onEdit: (value: string) => void;
  className?: string;
}

const EditableDiv: React.FC<EditableDivProps> = ({
  text,
  onEdit,
  className = "",
}) => {
  return (
    <div
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onEdit(e.currentTarget.textContent || "")}
      className={`outline-none ${className}`}
    >
      {text}
    </div>
  );
};

const DataSlide1 = {
  id: "data-slide1",
  name: "Data Visualization (Bar Chart)",
  fields: [
    { id: "title", label: "Title", type: "text" },
    { id: "subtitle", label: "Subtitle", type: "text" },
    { id: "data", label: "Data", type: "text" },
    { id: "teacherNote", label: "Teacher Note", type: "text" },
    { id: "bgColor", label: "Background Color", type: "text" },
  ],
  render: (content: SlideContent = {}) => {
    const {
      title = "",
      subtitle = "",
      teacherNote = "",
      bgColor = "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
      data = "[]",
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);
    };

    let parsedData: DataPoint[] = [];
    try {
      if (typeof data === "string") {
        parsedData = JSON.parse(data);
      } else if (Array.isArray(data)) {
        parsedData = data;
      }
    } catch (error) {
      console.warn("Error parsing data:", error);
    }

    const CustomTooltip: React.FC<{
      active?: boolean;
      payload?: Array<{
        payload: DataPoint;
      }>;
      label?: string;
    }> = ({ active, payload, label }) => {
      if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
          <div className="bg-white/90 p-3 rounded-lg shadow-lg">
            <div className="flex items-center gap-2">
              <span className="text-xl">{data.icon}</span>
              <span className="font-semibold">{label}</span>
            </div>
            <p className="text-sm mt-1">{data.description}</p>
            <div className="font-bold mt-1">{data.value}%</div>
          </div>
        );
      }
      return null;
    };

    const handleDataEdit = (
      index: number,
      field: keyof DataPoint,
      value: string,
    ) => {
      const updatedData = [...parsedData];
      updatedData[index] = {
        ...updatedData[index],
        [field]: field === "value" ? Number(value.replace("%", "")) : value,
      };

      handleEdit("data")(JSON.stringify(updatedData));
    };

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
              backgroundSize: "40px 40px",
            }}
          />
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col h-full p-6">
          {/* Header */}
          <div className="text-center mb-4">
            <EditableDiv
              text={title}
              onEdit={handleEdit("title")}
              className="text-3xl font-bold mb-1"
            />
            <EditableDiv
              text={subtitle}
              onEdit={handleEdit("subtitle")}
              className="text-xl text-white/80"
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 flex">
            {/* Chart */}
            <div className="w-1/2 h-full bg-white/5 rounded-xl p-4 backdrop-blur-sm">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={parsedData}
                  margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.1)"
                  />
                  <XAxis dataKey="name" tick={{ fill: "#fff", fontSize: 12 }} />
                  <YAxis tick={{ fill: "#fff", fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="value"
                    fill="url(#barGradient)"
                    radius={[4, 4, 0, 0]}
                  />
                  <defs>
                    <linearGradient
                      id="barGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#60a5fa" stopOpacity={1} />
                      <stop
                        offset="100%"
                        stopColor="#3b82f6"
                        stopOpacity={0.8}
                      />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Description */}
            <div className="w-1/2 pl-4 flex flex-col gap-4">
              {parsedData.map((item, index) => (
                <div
                  key={index}
                  className="bg-white/10 rounded-lg p-3 backdrop-blur-sm"
                  style={{ borderLeft: `4px solid #60a5fa` }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{item.icon}</span>
                    <EditableDiv
                      text={item.name}
                      onEdit={(value) => handleDataEdit(index, "name", value)}
                      className="font-semibold"
                    />
                    <EditableDiv
                      text={`${item.value}%`}
                      onEdit={(value) => handleDataEdit(index, "value", value)}
                      className="ml-auto font-bold"
                    />
                  </div>
                  <EditableDiv
                    text={item.description}
                    onEdit={(value) =>
                      handleDataEdit(index, "description", value)
                    }
                    className="text-sm text-white/80"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Teacher Note */}
          <div className="mt-4 text-center">
            <EditableDiv
              text={teacherNote}
              onEdit={handleEdit("teacherNote")}
              className="text-sm text-white/70 italic"
            />
          </div>
        </div>
      </div>
    );
  },
};

export default DataSlide1;
